import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { motion } from 'framer-motion';
import { MessageSquare, Loader2, Send, Users } from 'lucide-react';
import { toast } from 'sonner';

const MAX = 1500;
const SMS_LIMIT = 160;

export default function CoachSmsBroadcast() {
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState({ valid: 0, invalid: 0, loading: true });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.smsAudience();
        setAudience({ valid: res.data.valid_recipients, invalid: res.data.invalid_format, loading: false });
      } catch {
        setAudience({ valid: 0, invalid: 0, loading: false });
      }
    })();
  }, []);

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    if (audience.valid === 0) {
      toast.error('No players have opted in for SMS yet.');
      return;
    }
    const ok = window.confirm(
      `Send this SMS to ${audience.valid} player${audience.valid !== 1 ? 's' : ''}?\n\n— ${trimmed.slice(0, 200)}${trimmed.length > 200 ? '…' : ''}`
    );
    if (!ok) return;
    setSending(true);
    setLastResult(null);
    try {
      const res = await adminAPI.smsBroadcast(trimmed);
      const d = res.data;
      setLastResult(d);
      toast.success(`Sent: ${d.sent} · Failed: ${d.failed.length} · Skipped: ${d.skipped.length}`);
      setContent('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    }
    setSending(false);
  };

  const segments = Math.max(1, Math.ceil(content.length / SMS_LIMIT));

  return (
    <Layout title="SMS BROADCAST" showBack>
      <div className="px-4 py-6 space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-[#10B981]" />
            <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]">Audience</h3>
          </div>
          <p className="text-sm text-[#0A2242]">
            <span className="font-bold text-[#10B981]" data-testid="sms-audience-count">
              {audience.loading ? '...' : audience.valid}
            </span> active player{audience.valid !== 1 ? 's' : ''} will receive this text.
          </p>
          <p className="text-[10px] text-[#94A3B8] mt-1">
            Only players who opted in (saved a phone number + checked SMS opt-in on their profile).
            {audience.invalid > 0 && ` ${audience.invalid} have an invalid phone format and will be skipped.`}
          </p>
        </motion.div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3">
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B]">Message</h3>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, MAX))}
            placeholder="e.g. New weekly challenge live! Open Elev802 Connected to watch + submit your attempt."
            rows={6}
            className="w-full p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
            style={{ fontSize: '16px' }}
            data-testid="sms-content"
          />
          <div className="flex justify-between text-[10px] text-[#94A3B8]">
            <span>{content.length} / {MAX} chars</span>
            <span>{segments} SMS segment{segments !== 1 ? 's' : ''} per recipient</span>
          </div>
          <button
            onClick={handleSend}
            disabled={sending || !content.trim() || audience.valid === 0}
            className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg py-3 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            data-testid="sms-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Sending...' : `Send to ${audience.valid} Player${audience.valid !== 1 ? 's' : ''}`}
          </button>
        </div>

        {lastResult && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2 text-sm" data-testid="sms-last-result">
            <p className="text-[#10B981] font-bold">✓ Delivered to {lastResult.sent}</p>
            {lastResult.failed.length > 0 && (
              <details className="text-xs text-[#64748B]">
                <summary className="cursor-pointer">Failed ({lastResult.failed.length})</summary>
                <ul className="ml-4 mt-1 list-disc space-y-0.5">
                  {lastResult.failed.map((f, i) => <li key={i}>{f.email} — {f.error}</li>)}
                </ul>
              </details>
            )}
            {lastResult.skipped.length > 0 && (
              <details className="text-xs text-[#64748B]">
                <summary className="cursor-pointer">Skipped ({lastResult.skipped.length})</summary>
                <ul className="ml-4 mt-1 list-disc space-y-0.5">
                  {lastResult.skipped.map((s, i) => <li key={i}>{s.email} — {s.reason}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3">
          <p className="text-[10px] text-[#64748B] leading-snug">
            <MessageSquare size={10} className="inline mr-1" />
            Twilio charges per SMS segment (~$0.0083 per segment in the US). 160-char messages = 1 segment. Longer messages split into multiple segments.
          </p>
        </div>
      </div>
    </Layout>
  );
}
